import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  ContinueLearningCard,
  PlaceholderCourseCard,
  CourseCard,
  SimulationCard,
  PlaceholderContinueLearningCard,
} from "./card";

import InfiniteScroll from "src/components/infiniteScroll";
import { Skill, UpdateSkillPreference } from "src/types/coaching/courses";
import { fetchMyCourses } from "src/redux/actions/coaching/learners";
import { fetchPreferencesSkills, fetchPublicCourses } from "src/redux/actions/coaching/explore";
import { fetchTeamsMostEnrolledLearningsReport } from "src/redux/actions/coaching/reports";

import {
  fetchSkills,
  invalidateSkills,
  updateSkillsPreference,
} from "src/redux/actions/coaching/skills";

import _ from "lodash";
import cx from "classnames";
import styles from "./explore.module.scss";
import { loading } from "src/localization/english/commons";

const ExploreCoaching: React.FC<object> = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => _.get(state, "users.me"));

  useEffect(() => {
    dispatch(fetchPublicCourses({courseType: "ALL", page: 0, limit: 10}));
    dispatch(fetchPublicCourses({courseType: "SIMULATION", page: 0, limit: 10}));
    dispatch(fetchMyCourses({ status: "IN_PROGRESS", page: 0, limit: 10 }));
    dispatch(fetchSkills({
      query: "",
      parent: null,
      page: 0,
      limit: 20,
    }));
    dispatch(fetchPreferencesSkills());
    dispatch(fetchTeamsMostEnrolledLearningsReport({}));

    return () => {
      dispatch(invalidateSkills());
    };
  }, []);

  return (
    <section className={styles.container}>
      <div className={styles.intro}>
        <h3>Welcome back, {user.firstName}!</h3>
        <p className={styles.learn}>What would you like to learn today?</p>
      </div>

      <PublicCourses type="SIMULATION" />
      <ContinueLearnings />
      <PublicCourses type="ALL" />
      <PopularLearnings />
      <PersonalisedSkills />
    </section>
  );
};

export { ExploreCoaching };

interface PublicCoursesProps {
  type: string;
}

const PublicCourses: React.FC<PublicCoursesProps> = ({type}) => {
  const { loading, learnings, hasNext, nextPage, total } = useSelector(
    (state: any) => {
      let courseType = "all"
      if (type === "COURSE") {
        courseType = "courses";
      } else if (type === "SIMULATION") {
        courseType = "simulations";
      }

      const learningsState = _.get(state.coaching.explore.public, courseType);
      const loading = _.get(learningsState, "loading");
      const learnings = _.get(learningsState, "items") || [];
      const pagination = _.get(learningsState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasNext = _.get(pagination, "hasMoreItems", false);

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        learnings,
        loading,
        hasNext,
        nextPage,
        total,
      };
    },
    _.isEqual,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchLearningsForPage = (page: number) => {
    dispatch(fetchPublicCourses({
      courseType: type,
      page,
      limit: 10,
    }));
  };

  const handleLoadMoreLearnings = () => {
    if (nextPage) {
      fetchLearningsForPage(nextPage);
    }
  };

  const handleClick = (item) => {
    if (item.entityType === "course") {
      navigate(`/coaching/explore/courses/${item.id}`);
    } else if (item.entityType === "simulation") {
      navigate(`/coaching/explore/simulations/${item.id}`);
    }
  };

  const getHeader = (type: string) => {
    if (type === "ALL" || type === "COURSE") {
      return <header className={styles.title}>recommended for the skills you’re building</header>;
    }

    if (type === "SIMULATION") {
      return <header className={styles.title}>Try a dry run for your next calls</header>;
    } 
  };

  return (
    <section className={cx(styles.sectionContainer, styles.popularLearnings)}>

      {getHeader(type)}

      <div className={styles.learnings}>
        <InfiniteScroll
          className={styles.courseList}
          loading={loading}
          hasMore={hasNext}
          loadMore={handleLoadMoreLearnings}
          loader={() => {
            return _.times(_.size(learnings) == 0 ? 4 : 1).map((i) => (
              <PlaceholderCourseCard key={i} />
            ));
          }}
        >
          {learnings.map((item, index) => (
            <div key={index} className={styles.wrapper}>
              {item.entityType === "course" && (
                <CourseCard
                  key={item.id}
                  course={item.course}
                  onClick={() => handleClick(item)}
                />
              )}

              {item.entityType === "simulation" && (
                <SimulationCard
                  key={item.id}
                  course={item.course}
                  onClick={() => handleClick(item)}
                />
              )}
            </div>
          ))}
        </InfiniteScroll>
      </div>
    </section>
  );
};

interface PersonalisedSkillsProps {}

const PersonalisedSkills: React.FC<PersonalisedSkillsProps> = () => {
  const dispatch = useDispatch();

  const skills = useSelector((state: any) =>
    _.get(state, "courseSkills.items", []),
  );

  const selectedPreferenceSkills = useSelector((state: any) =>
    _.get(state.coaching, "explore.preferences.selectedSkills", []),
  );

  const handleClick = (skill: Skill) => {
    const selected = _.some(selectedPreferenceSkills, { id: skill.id });

    let updatedSkills;

    if (selected) {
      updatedSkills = selectedPreferenceSkills.map(
        (s: UpdateSkillPreference) =>
          s.id === skill.id ? { ...s, selected: false } : s,
      );
    } else {
      updatedSkills = [
        ...selectedPreferenceSkills,
        { id: skill.id, selected: true },
      ];
    }

    const request = {
      skills: updatedSkills,
    };

    dispatch(updateSkillsPreference(request));
  };

  if(loading && skills.length === 0) {
    return (
      <div></div>
    )
  }

  return (
    <section
      className={cx(styles.sectionContainer, styles.predefinedLearnings)}
    >
      <div className={styles.skills}>
        <h3 className={styles.title}>personalised topics for you</h3>

        <div className={styles.items}>
          {skills.map((skill: Skill) => {
            const selected = _.some(selectedPreferenceSkills, { id: skill.id });
            return (
              <div
                key={skill.id}
                className={cx(styles.item, {
                  [styles.selected]: selected,
                })}
                onClick={() => handleClick(skill)}
              >
                {selected && <i className="paperflite-tick" />}
                {skill.name}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

interface ContinueLearningsProps {}

const ContinueLearnings: React.FC<ContinueLearningsProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { learnings, loading, hasNext, nextPage, total } = useSelector(
    (state) => {
      const learningState = _.get(state, `learners.courses.inProgress`);

      const learnings = _.get(learningState, "items") || [];
      const loading = _.get(learningState, "loading");
      const pagination = _.get(learningState, "pagination");
      const total = _.get(pagination, "totalPages", 0);
      const hasNext = _.get(pagination, "hasNext", false);

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        learnings,
        loading,
        hasNext,
        nextPage,
        total,
      };
    },
    _.isEqual,
  );

  const fetchLearningsForPage = (page: number) => {
    dispatch(fetchMyCourses({ status: "IN_PROGRESS", page, limit: 20 }));
  };

  const handleLoadMoreLearnings = () => {
    if (nextPage) {
      fetchLearningsForPage(nextPage);
    }
  };

  const handleClick = (record) => {
    const entityType = _.get(record, "course.entityType");
    if(entityType === "course") {
      navigate(`/coaching/courses/${record.id}`);
    } else if (entityType === "simulation") {
      navigate(`/coaching/simulations/${record.id}`);
    }
  };

  return (
    <section className={cx(styles.sectionContainer, styles.continueLearnings)}>
      <header className={styles.title}>pick up where you left off</header>

      <div className={styles.learnings}>
        <InfiniteScroll
          className={styles.continueLearningList}
          loading={loading}
          hasMore={hasNext}
          loadMore={handleLoadMoreLearnings}
          loader={() => {
            return _.times(_.size(learnings) == 0 ? 4 : 1).map((i) => (
              <PlaceholderContinueLearningCard key={i} />
            ));
          }}
        >
          {learnings.map((item: any) => {
            return (
              <ContinueLearningCard
                key={item.id}
                course={item}
                onClick={() => handleClick(item)}
              />
            );
          })}
        </InfiniteScroll>
      </div>
    </section>
  );
};

interface PopularLearningsProps {}

const PopularLearnings: React.FC<PopularLearningsProps> = () => {
  const navigate = useNavigate();

  const { learnings, loading } = useSelector((state) => {
    const reportsState = _.get(
      state,
      "coaching.reports.teams.mostEnrolledLearnings",
    );

    const learnings = _.get(reportsState, "items") || [];
    const loading = _.get(reportsState, "loading");

    return {
      learnings,
      loading,
    };
  }, _.isEqual);

  const handleClick = (item) => {
    if (item.type === "COURSE") {
      navigate(`/coaching/explore/courses/${item.course?.id}`);
    } else if (item.type === "SIMULATION") {
      navigate(`/coaching/explore/simulations/${item.course?.id}`);
    }
  };

  return (
    <section className={cx(styles.sectionContainer, styles.popularLearnings)}>
      <header className={styles.title}>
        learnings, popular with your team
      </header>

      <div className={styles.learnings}>
        <InfiniteScroll
          className={styles.courseList}
          loading={loading}
          hasMore={false}
          loadMore={() => {}}
          loader={() => {
            return _.times(_.size(learnings) == 0 ? 4 : 1).map((i) => (
              <PlaceholderCourseCard key={i} />
            ));
          }}
        >
          {learnings.map((item: any, index) => (
            <div key={index} className={styles.wrapper}>
              <div className={styles.number}>{index + 1}</div>
              {item?.course?.entityType === "course" && (
                <CourseCard
                  key={item.id}
                  course={item.course}
                  onClick={() => handleClick(item)}
                />
              )}

              {item?.course?.entityType === "simulation" && (
                <SimulationCard
                  key={item.id}
                  course={item.course}
                  onClick={() => handleClick(item)}
                />
              )}
            </div>
          ))}
        </InfiniteScroll>
      </div>
    </section>
  );
};
